import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Inbox, Check, X, MessageSquare, Loader2, Star, ShieldCheck, ArrowLeft } from 'lucide-react';
import { API } from '../utils/apiConfig';

/**
 * Driver-facing inbox of incoming match requests.
 * Live-updates via Socket.IO `match:request` events when a new one arrives.
 */
export default function MatchInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState('');
  // Reject-reason modal
  const [rejectFor, setRejectFor] = useState(null);  // request being rejected

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/match/requests/incoming`);
      setRequests(r.data?.requests || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'load_failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Light polling every 8s + reload on window focus
  useEffect(() => {
    const t1 = setInterval(load, 8000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t1); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const accept = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await axios.post(`${API}/match/requests/${id}/accept`);
      setRequests(rs => rs.filter(r => r.id !== id));
    } catch (e) {
      setError(e.response?.data?.detail || 'decide_failed');
    }
    setBusy(b => ({ ...b, [id]: false }));
  };

  const submitReject = async (reason, message) => {
    if (!rejectFor) return;
    const id = rejectFor.id;
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await axios.post(`${API}/match/requests/${id}/reject`, { reason, message });
      setRequests(rs => rs.filter(r => r.id !== id));
      setRejectFor(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'decide_failed');
    }
    setBusy(b => ({ ...b, [id]: false }));
  };

  return (
    <div data-testid="match-inbox-page" style={st.page}>
      <div style={st.header}>
        <button data-testid="match-inbox-back" onClick={() => navigate('/dashboard')} style={st.backBtn}>
          <ArrowLeft size={16} /> {t('common.back', 'Zurück')}
        </button>
        <h1 style={st.title}>
          <Inbox size={20} /> {t('match.inboxTitle', 'Mitfahranfragen')}
        </h1>
        <span data-testid="match-inbox-count" style={st.count}>{requests.length}</span>
      </div>

      {loading && (
        <div data-testid="match-inbox-loading" style={st.empty}>
          <Loader2 size={20} className="spin" /> {t('common.loading', 'Lade…')}
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div data-testid="match-inbox-empty" style={st.empty}>
          {t('match.inboxEmpty', 'Keine offenen Anfragen.')}
        </div>
      )}

      {error && (
        <div data-testid="match-inbox-error" style={st.error}>{error}</div>
      )}

      <div style={st.list}>
        {requests.map(r => (
          <RequestCard key={r.id} req={r} busy={!!busy[r.id]}
            onAccept={() => accept(r.id)}
            onReject={() => setRejectFor(r)}
            onProfile={() => navigate(`/profile/${r.passenger?.id}`)} />
        ))}
      </div>

      {rejectFor && (
        <RejectReasonModal
          req={rejectFor}
          busy={!!busy[rejectFor.id]}
          onClose={() => setRejectFor(null)}
          onSubmit={submitReject}
        />
      )}
    </div>
  );
}


const RequestCard = ({ req, busy, onAccept, onReject, onProfile }) => {
  const { t } = useTranslation();
  const p = req.passenger || {};
  const initials = (p.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div data-testid={`match-req-${req.id}`} style={st.card}>
      <button onClick={onProfile} style={st.avatarBtn} data-testid={`match-req-${req.id}-profile`}>
        {p.profile_picture_url
          ? <img src={p.profile_picture_url} alt="" style={st.avatarImg} />
          : <div style={st.avatarFallback}>{initials}</div>}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={st.cardTop}>
          <strong style={st.name}>{p.name || '—'}</strong>
          {p.verified && <ShieldCheck size={14} color="#22c55e" />}
          {p.rating != null && (
            <span style={st.rating}>
              <Star size={12} color="#f59e0b" /> {p.rating.toFixed(1)}
            </span>
          )}
        </div>
        <div style={st.bio}>{p.bio || p.city || ''}</div>
        {req.message && (
          <div data-testid={`match-req-${req.id}-msg`} style={st.message}>
            <MessageSquare size={12} /> {req.message}
          </div>
        )}
      </div>
      <div style={st.actions}>
        <button data-testid={`match-req-${req.id}-reject`} onClick={onReject} disabled={busy}
          style={{ ...st.actionBtn, ...st.rejectBtn }}>
          <X size={14} /> {busy ? '…' : t('match.decline', 'Ablehnen')}
        </button>
        <button data-testid={`match-req-${req.id}-accept`} onClick={onAccept} disabled={busy}
          style={{ ...st.actionBtn, ...st.acceptBtn }}>
          <Check size={14} /> {busy ? '…' : t('match.accept', 'Annehmen')}
        </button>
      </div>
    </div>
  );
};


/**
 * Friendly modal letting the driver pick a reject-reason
 * (ausgebucht / falsche Strecke / Zeitkollision / sonstiges + freier Text).
 */
const RejectReasonModal = ({ req, busy, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('fully_booked');
  const [message, setMessage] = useState('');
  const presets = [
    { code: 'fully_booked',   label: t('match.reject.fullyBooked',   'Schon ausgebucht') },
    { code: 'route_mismatch', label: t('match.reject.routeMismatch', 'Andere Strecke') },
    { code: 'time_mismatch',  label: t('match.reject.timeMismatch',  'Zeit passt nicht') },
    { code: 'other',          label: t('match.reject.other',         'Sonstiges') },
  ];
  const submit = (e) => {
    e?.preventDefault?.();
    onSubmit(reason, message.trim().slice(0, 280));
  };
  return (
    <div data-testid="match-reject-modal" style={st.modalBackdrop} onClick={onClose}>
      <form
        data-testid="match-reject-form"
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={st.modalCard}
      >
        <div style={st.modalHeader}>
          <strong>{t('match.reject.title', 'Anfrage ablehnen')}</strong>
          <button type="button" onClick={onClose} style={st.modalClose} data-testid="match-reject-close">
            <X size={16} />
          </button>
        </div>

        <p style={st.modalLead}>
          {t('match.reject.lead', 'Wähle einen Grund — {{name}} bekommt eine kurze freundliche Erklärung.', {
            name: req?.passenger?.name || t('common.passenger', 'Mitfahrer:in'),
          })}
        </p>

        <div style={st.reasonGrid}>
          {presets.map(p => {
            const selected = reason === p.code;
            return (
              <button
                key={p.code}
                type="button"
                aria-pressed={selected}
                data-testid={`match-reject-reason-${p.code}`}
                onClick={() => setReason(p.code)}
                style={{
                  ...st.reasonChip,
                  ...(selected ? st.reasonChipActive : {}),
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <textarea
          data-testid="match-reject-message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={t('match.reject.placeholder', 'Optional: Persönliche Notiz (max. 280 Zeichen)')}
          maxLength={280}
          rows={3}
          style={st.textarea}
        />

        <div style={st.modalActions}>
          <button
            type="button"
            onClick={onClose}
            style={{ ...st.actionBtn, ...st.cancelBtn }}
            data-testid="match-reject-cancel"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            type="submit"
            disabled={busy}
            style={{ ...st.actionBtn, ...st.confirmRejectBtn, opacity: busy ? 0.6 : 1 }}
            data-testid="match-reject-confirm"
          >
            {busy
              ? t('common.loading', 'Lade…')
              : t('match.reject.confirm', 'Ablehnung senden')}
          </button>
        </div>
      </form>
    </div>
  );
};


const st = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg,#0b1220 0%,#0f172a 60%,#020617 100%)',
    padding: '24px 16px', color: '#f1f5f9',
  },
  header: {
    maxWidth: 760, margin: '0 auto 18px',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  backBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '6px 10px', color: '#cbd5e1', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
  },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, margin: 0 },
  count: {
    marginLeft: 'auto', background: 'rgba(99,102,241,0.18)', color: '#c7d2fe',
    borderRadius: 999, padding: '2px 10px', fontSize: 13, fontWeight: 600,
  },
  list: { maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    display: 'flex', gap: 14, padding: 16, borderRadius: 14,
    background: 'rgba(15,23,42,0.78)', border: '1px solid rgba(255,255,255,0.07)',
    alignItems: 'flex-start',
  },
  avatarBtn: { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' },
  avatarImg: { width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg,#0ea5e9,#00d4ff)',
    color: '#fff', fontWeight: 700, fontSize: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  name: { fontSize: 15, color: '#f1f5f9' },
  rating: { display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#fbbf24' },
  bio: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  message: {
    marginTop: 8, padding: '6px 10px', borderRadius: 10,
    background: 'rgba(255,255,255,0.04)', fontSize: 13, color: '#cbd5e1',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  actions: { display: 'flex', flexDirection: 'column', gap: 8 },
  actionBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', border: 'none',
  },
  acceptBtn: { background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff' },
  rejectBtn: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)' },
  empty: { maxWidth: 760, margin: '40px auto', textAlign: 'center', color: '#64748b', fontSize: 14, display: 'flex', justifyContent: 'center', gap: 8 },
  error: { maxWidth: 760, margin: '0 auto 12px', padding: 10, borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#fca5a5', fontSize: 13 },

  // ── Reject-reason modal ──
  modalBackdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(2,6,23,0.72)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 50, padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 460,
    background: 'linear-gradient(180deg,#0f172a,#0b1120)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18, padding: 22,
    boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
    color: '#f1f5f9',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    fontSize: 16, marginBottom: 6,
  },
  modalClose: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
    color: '#cbd5e1', borderRadius: 8, padding: 4, cursor: 'pointer',
  },
  modalLead: { color: '#94a3b8', fontSize: 13, lineHeight: 1.45, margin: '4px 0 14px' },
  reasonGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
    marginBottom: 12,
  },
  reasonChip: {
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#cbd5e1',
    borderRadius: 10, padding: '10px 12px', fontSize: 13,
    cursor: 'pointer', textAlign: 'left', fontWeight: 500,
  },
  reasonChipActive: {
    background: 'linear-gradient(135deg, rgba(59,130,246,0.22), rgba(139,92,246,0.22))',
    border: '1px solid rgba(99,102,241,0.55)',
    color: '#e0e7ff',
    boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.3)',
  },
  textarea: {
    width: '100%', resize: 'vertical', minHeight: 70,
    background: 'rgba(2,6,23,0.5)', color: '#f1f5f9',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
    padding: 10, fontSize: 13, marginBottom: 14, outline: 'none',
    fontFamily: 'inherit',
  },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: {
    background: 'rgba(255,255,255,0.06)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  confirmRejectBtn: {
    background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff',
    boxShadow: '0 6px 18px rgba(239,68,68,0.32)',
  },
};
